import { motion } from 'framer-motion';

interface ServiceHeroProps {
  icon: string;
  label: string;
  title: string;
  subtitle: string;
  bgImage: string;
}

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } },
};

export default function ServiceHero({ icon, label, title, subtitle, bgImage }: ServiceHeroProps) {
  return (
    <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
      {/* Background */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={bgImage}
          alt={title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1419]/75 via-[#0f1419]/60 to-[#0f1419]"></div>
      </div>

      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 50% 60%, rgba(0,212,255,0.10) 0%, transparent 60%)'
      }}></div>

      {/* Content */}
      <div className="relative z-10 max-w-[900px] mx-auto px-6 lg:px-8 text-center w-full py-24">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center"
        >
          <motion.div variants={itemVariants}>
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-[rgba(0,212,255,0.15)] border border-[rgba(0,212,255,0.3)] mb-8 mx-auto backdrop-blur-sm">
              <i className={`${icon} text-[#00d4ff] text-4xl`}></i>
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <div className="inline-block mb-5 px-5 py-1.5 rounded-full bg-[rgba(0,212,255,0.12)] border border-[rgba(0,212,255,0.25)]">
              <span className="text-[#00d4ff] text-xs font-semibold uppercase tracking-[2px] whitespace-nowrap">
                {label}
              </span>
            </div>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6"
          >
            {title}
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-[rgba(255,255,255,0.65)] text-lg md:text-xl leading-relaxed max-w-[680px] mx-auto"
          >
            {subtitle}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
